import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Button,
  ListItemButton,
} from "@mui/material";
import {
  ShoppingCart,
  Person,
  ExpandMore,
  ExpandLess,
  LocalShipping,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { QUDA_COLORS } from "../config/colors";
import { phappsRequest } from "../services/apiService";
import { API_PATHS } from "../config/apiConstants";
import PieChart from "./PieChart";
import SupplierOrdersModal from "./SupplierOrdersModal";

const QudaAnalytics = ({ chemistData, chemistID }) => {
  const [showAllTopItems, setShowAllTopItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemChartData, setItemChartData] = useState(null);
  const [loadingItemData, setLoadingItemData] = useState(false);

  // Supplier Modal States
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierOrders, setSupplierOrders] = useState([]);
  const [loadingSupplierData, setLoadingSupplierData] = useState(false);
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);

  // Format data for charts
  const formatChartData = (data, dateField, countField) => {
    if (!data) return [];
    return data.map((item) => ({
      date: new Date(item[dateField]).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      count: item[countField],
    }));
  };

  // Initial chart data from overall chemist stats
  const overallOrdersChartData = formatChartData(
    chemistData?.qudaStats?.ordersPerDay || [],
    "date",
    "orderCount"
  );

  // Determine which data to show in the chart
  const currentChartData = itemChartData || overallOrdersChartData;
  const chartTitle = selectedItem
    ? `Orders Per Day: ${selectedItem}`
    : "Orders Per Day";

  // Type split pie chart data
  console.log('ChemistData structure:', chemistData);
  const dispenseShopSplit = chemistData?.qudaStats?.dispenseShopSplit || chemistData?.dispenseShopSplit;
  const typeSplitData = dispenseShopSplit ? [
    {
      x: "Shop Orders",
      y: parseInt(dispenseShopSplit.shopOrders) || 0,
      text: "Shop Orders",
      color: QUDA_COLORS.TEAL_DARK,
    },
    {
      x: "Dispense Orders",
      y: parseInt(dispenseShopSplit.dispenseOrders) || 0,
      text: "Dispense Orders",
      color: QUDA_COLORS.PURPLE_DARK,
    },
  ].filter((d) => d.y > 0) : [];

  const handleItemClick = async (itemName) => {
    // If clicking the same item, deselect it
    if (selectedItem === itemName) {
      setSelectedItem(null);
      setItemChartData(null);
      return;
    }

    setSelectedItem(itemName);
    setLoadingItemData(true);

    try {
      // Calculate date range (last 30 days as default, similar to main fetch)
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 30);

      const dateStart = startDate.toISOString().split("T")[0];
      const dateEnd = today.toISOString().split("T")[0];

      // Construct the dynamic URL with query params
      // URL pattern: /managerv1/qudaV1/ItemOrders?PHUUID=...&ItemName=...&DateStart=...&DateEnd=...
      const params = {
        PHUUID: chemistID,
        ItemName: itemName,
        DateStart: dateStart,
        DateEnd: dateEnd,
      };

      const response = await phappsRequest(
        API_PATHS.ITEM_ORDERS,
        "GET",
        null,
        params
      );

      if (response && response.ItemOrders && response.ItemOrders.ordersPerDay) {
        const formattedData = formatChartData(
          response.ItemOrders.ordersPerDay,
          "date",
          "orderCount"
        );
        setItemChartData(formattedData);
      }
    } catch (error) {
      console.error("Failed to fetch item orders:", error);
    } finally {
      setLoadingItemData(false);
    }
  };

  const handleSupplierClick = async (supplierName) => {
    setSelectedSupplier(supplierName);
    setIsSupplierModalOpen(true);
    setLoadingSupplierData(true);
    setSupplierOrders([]);

    try {
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 30);

      const dateStart = startDate.toISOString().split("T")[0];
      const dateEnd = today.toISOString().split("T")[0];

      const params = {
        PHUUID: chemistID,
        SupplierName: supplierName,
        DateStart: dateStart,
        DateEnd: dateEnd,
      };

      const response = await phappsRequest(
        API_PATHS.SUPPLIER_ORDERS,
        "GET",
        null,
        params
      );
      
      // Based on user provided example data structure:
      // { "SupplierOrders": [ { "ItemName": "...", "count": 1 }, ... ] }
      if (response && response.SupplierOrders) {
        const mappedOrders = (
          Array.isArray(response.SupplierOrders) ? response.SupplierOrders : []
        ).map((item) => ({
          itemName: item.ItemName,
          orderCount: item.count,
        }));
        setSupplierOrders(mappedOrders);
      } else if (Array.isArray(response)) {
        // Fallback if the array is returned directly
        const mappedOrders = response.map((item) => ({
          itemName: item.ItemName,
          orderCount: item.count,
        }));
        setSupplierOrders(mappedOrders);
      }

    } catch (error) {
      console.error("Failed to fetch supplier orders:", error);
    } finally {
      setLoadingSupplierData(false);
    }
  };

  const handleCloseSupplierModal = () => {
    setIsSupplierModalOpen(false);
    setSelectedSupplier(null);
    setSupplierOrders([]);
  };

  return (
    <Box sx={{ flexGrow: 1, mt: 2 }}>
      <SupplierOrdersModal 
        open={isSupplierModalOpen} 
        onClose={handleCloseSupplierModal} 
        supplierName={selectedSupplier}
        orders={supplierOrders}
        loading={loadingSupplierData}
      />

      <Grid container spacing={3}>
        {/* Left: Orders Chart */}
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              border: `2px solid ${QUDA_COLORS.TEAL_DARK}`,
              borderRadius: "8px",
              overflow: "hidden",
              height: "100%",
            }}
          >
            <Card sx={{ height: "100%", boxShadow: "none" }}>
              <CardContent>
                <Box className="flex items-center justify-between mb-4">
                  <Box className="flex items-center space-x-2">
                    <ShoppingCart sx={{ color: QUDA_COLORS.TEAL_DARK }} />
                    <Typography
                      variant="h6"
                      className="font-bold"
                      sx={{ color: QUDA_COLORS.TEAL_DARK }}
                    >
                      {chartTitle}
                    </Typography>
                  </Box>
                  {selectedItem && (
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedItem(null);
                        setItemChartData(null);
                      }}
                      sx={{ color: QUDA_COLORS.TEAL_DARK }}
                    >
                      Clear Selection
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2, borderColor: QUDA_COLORS.TEAL_LIGHT }} />
                <Box sx={{
                  "& .recharts-wrapper": {
                    "&:focus": { outline: "none" },
                    "&:focus-visible": { outline: "none" },
                    "& svg": {
                      "&:focus": { outline: "none" },
                      "&:focus-visible": { outline: "none" }
                    }
                  }
                }}>
                  <ResponsiveContainer width="100%" height={400}>
                  {loadingItemData ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="100%"
                    >
                      <Typography>Loading...</Typography>
                    </Box>
                  ) : (
                    <LineChart data={currentChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis
                        dataKey="date"
                        stroke={QUDA_COLORS.TEAL_DARK}
                        fontSize={12}
                      />
                      <YAxis stroke={QUDA_COLORS.TEAL_DARK} fontSize={12} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "white",
                          border: `1px solid ${QUDA_COLORS.TEAL_DARK}`,
                          borderRadius: "8px",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={QUDA_COLORS.TEAL_DARK}
                        strokeWidth={3}
                        dot={{
                          fill: QUDA_COLORS.TEAL_LIGHT,
                          strokeWidth: 2,
                          r: 4,
                        }}
                        activeDot={{
                          r: 6,
                          stroke: QUDA_COLORS.TEAL_DARK,
                          strokeWidth: 2,
                        }}
                      />
                    </LineChart>
                  )}
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Right: Top Items */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              border: `2px solid ${QUDA_COLORS.TEAL_DARK}`,
              borderRadius: "8px",
              overflow: "hidden",
              height: "100%",
            }}
          >
            <Card sx={{ height: "100%", boxShadow: "none" }}>
              <CardContent>
                <Box className="flex items-center space-x-2 mb-4">
                  <ShoppingCart sx={{ color: QUDA_COLORS.TEAL_DARK }} />
                  <Typography
                    variant="h6"
                    className="font-bold"
                    sx={{ color: QUDA_COLORS.TEAL_DARK }}
                  >
                    Top 5 Items by Orders
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontStyle: "italic", display: "block", mb: 1, mt: -1 }}
                >
                  Select an item to view order data
                </Typography>
                <Divider sx={{ mb: 2, borderColor: QUDA_COLORS.TEAL_LIGHT }} />
                {chemistData?.qudaStats?.topItems &&
                chemistData.qudaStats.topItems.length > 0 ? (
                  <>
                    <List
                      sx={{
                        maxHeight: showAllTopItems ? 400 : "auto",
                        overflow: "auto",
                      }}
                    >
                      {(showAllTopItems
                        ? chemistData.qudaStats.topItems
                        : chemistData.qudaStats.topItems.slice(0, 5)
                      ).map((item, index) => (
                        <ListItem key={index} className="px-0" disablePadding>
                          <ListItemButton
                            onClick={() => handleItemClick(item.itemName)}
                            selected={selectedItem === item.itemName}
                            sx={{
                              borderRadius: "8px",
                              "&.Mui-selected": {
                                backgroundColor: QUDA_COLORS.TEAL_LIGHT + "40", // 25% opacity
                                border: `1px solid ${QUDA_COLORS.TEAL_DARK}`,
                                "&:hover": {
                                  backgroundColor:
                                    QUDA_COLORS.TEAL_LIGHT + "60",
                                },
                              },
                            }}
                          >
                            <ListItemAvatar>
                              <Avatar
                                sx={{
                                  backgroundColor: QUDA_COLORS.TEAL_LIGHT,
                                  color: QUDA_COLORS.TEAL_DARK,
                                  fontWeight: "bold",
                                }}
                              >
                                {index + 1}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={item.itemName}
                              secondary={`${item.orderCount} order${
                                item.orderCount !== 1 ? "s" : ""
                              }`}
                              primaryTypographyProps={{ fontWeight: "medium" }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                    {chemistData.qudaStats.topItems.length > 5 && (
                      <Button
                        onClick={() => setShowAllTopItems(!showAllTopItems)}
                        startIcon={
                          showAllTopItems ? <ExpandLess /> : <ExpandMore />
                        }
                        fullWidth
                        sx={{
                          mt: 1,
                          color: QUDA_COLORS.TEAL_DARK,
                          borderColor: QUDA_COLORS.TEAL_DARK,
                          "&:hover": {
                            backgroundColor: QUDA_COLORS.TEAL_LIGHT,
                            borderColor: QUDA_COLORS.TEAL_DARK,
                          },
                        }}
                        variant="outlined"
                        size="small"
                      >
                        {showAllTopItems
                          ? "Show Less"
                          : `Show ${
                              chemistData.qudaStats.topItems.length - 5
                            } More`}
                      </Button>
                    )}
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className="text-center py-4"
                  >
                    No top items data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Second Row: Staff Lists, Suppliers and Type Split */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Left: Top 5 Staff */}
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              border: `2px solid ${QUDA_COLORS.TEAL_DARK}`,
              borderRadius: "8px",
              overflow: "hidden",
              height: "100%",
            }}
          >
            <Card sx={{ height: "100%", boxShadow: "none" }}>
              <CardContent>
                <Box className="flex items-center space-x-2 mb-4">
                  <Person sx={{ color: QUDA_COLORS.TEAL_DARK }} />
                  <Typography
                    variant="h6"
                    className="font-bold"
                    sx={{ color: QUDA_COLORS.TEAL_DARK }}
                  >
                    Top 5 Staff by Interactions
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2, borderColor: QUDA_COLORS.TEAL_LIGHT }} />
                {chemistData?.qudaStats?.top5Staff &&
                chemistData.qudaStats.top5Staff.length > 0 ? (
                  <List>
                    {chemistData.qudaStats.top5Staff.map((staff, index) => (
                      <ListItem key={index} className="px-0">
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: QUDA_COLORS.TEAL_LIGHT,
                              color: QUDA_COLORS.TEAL_DARK,
                              fontWeight: "bold",
                            }}
                          >
                            {index + 1}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={staff.staffName}
                          secondary={`${staff.interactionCount} interactions`}
                          primaryTypographyProps={{ fontWeight: "medium" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className="text-center py-4"
                  >
                    No top staff data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Center Left: Bottom 5 Staff */}
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              border: `2px solid ${QUDA_COLORS.TEAL_DARK}`,
              borderRadius: "8px",
              overflow: "hidden",
              height: "100%",
            }}
          >
            <Card sx={{ height: "100%", boxShadow: "none" }}>
              <CardContent>
                <Box className="flex items-center space-x-2 mb-4">
                  <Person sx={{ color: QUDA_COLORS.TEAL_DARK }} />
                  <Typography
                    variant="h6"
                    className="font-bold"
                    sx={{ color: QUDA_COLORS.TEAL_DARK }}
                  >
                    Bottom 5 Staff by Interactions
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2, borderColor: QUDA_COLORS.TEAL_LIGHT }} />
                {chemistData?.qudaStats?.bottom5Staff &&
                chemistData.qudaStats.bottom5Staff.length > 0 ? (
                  <List>
                    {chemistData.qudaStats.bottom5Staff.map((staff, index) => (
                      <ListItem key={index} className="px-0">
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: "#fde8e8", // Light red background for bottom staff
                              color: "#9b1c1c", // Dark red text
                              fontWeight: "bold",
                            }}
                          >
                            {index + 1}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={staff.staffName}
                          secondary={`${staff.interactionCount} interactions`}
                          primaryTypographyProps={{ fontWeight: "medium" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className="text-center py-4"
                  >
                    No bottom staff data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Center Right: Top 5 Suppliers */}
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              border: `2px solid ${QUDA_COLORS.TEAL_DARK}`,
              borderRadius: "8px",
              overflow: "hidden",
              height: "100%",
            }}
          >
            <Card sx={{ height: "100%", boxShadow: "none" }}>
              <CardContent>
                <Box className="flex items-center space-x-2 mb-4">
                  <LocalShipping sx={{ color: QUDA_COLORS.TEAL_DARK }} />
                  <Typography
                    variant="h6"
                    className="font-bold"
                    sx={{ color: QUDA_COLORS.TEAL_DARK }}
                  >
                    Top 5 Suppliers
                  </Typography>
                  
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontStyle: "italic", display: "block", mb: 1, mt: -1 }}
                >
                  Select a supplier to view order data
                </Typography>
                <Divider sx={{ mb: 2, borderColor: QUDA_COLORS.TEAL_LIGHT }} />
                {chemistData?.qudaStats?.topSuppliers &&
                chemistData.qudaStats.topSuppliers.length > 0 ? (
                  <List>
                    {chemistData.qudaStats.topSuppliers.map(
                      (supplier, index) => (
                        <ListItem key={index} className="px-0" disablePadding>
                          <ListItemButton 
                            onClick={() => handleSupplierClick(supplier.supplierName)}
                            sx={{
                              borderRadius: "8px",
                              "&:hover": {
                                backgroundColor: QUDA_COLORS.TEAL_LIGHT + "40",
                              },
                            }}
                          >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                backgroundColor: QUDA_COLORS.TEAL_LIGHT,
                                color: QUDA_COLORS.TEAL_DARK,
                                fontWeight: "bold",
                              }}
                            >
                              {index + 1}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={supplier.supplierName}
                            secondary={`${supplier.orderCount} orders`}
                            primaryTypographyProps={{ fontWeight: "medium" }}
                          />
                          </ListItemButton>
                        </ListItem>
                      )
                    )}
                  </List>
                ) : (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    className="text-center py-4"
                  >
                    No top supplier data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Right: Order Types Pie Chart */}
        <Grid item xs={12} md={3}>
          <PieChart
            title="Order Types"
            data={typeSplitData}
            height="100%"
            borderColor={QUDA_COLORS.TEAL_DARK}
            titleColor={QUDA_COLORS.TEAL_DARK}
            iconColor={QUDA_COLORS.TEAL_DARK}
            dividerColor={QUDA_COLORS.TEAL_LIGHT}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default QudaAnalytics;
